/* ============================================================
   Toee Tire - the product-page fit verdict.
   With a Current vehicle set, asks /apps/pro/vehicle/check whether this tire or wheel
   fits it and shows one block under the product title (snippets/toee-vehicle-verdict.liquid):
   solid blue (fits), blue outline (needs a hub ring), grey (another setup of the car fits,
   does not fit, or the centre bore is unknown). A failed request, a product that is neither a
   tire nor a wheel, or no vehicle: the block stays hidden.

   The answer lists every Fitment of the car, so the answers are kept in sessionStorage per
   product + car and a Fitment switch repaints without a request.

   verdictView is pure, for tests (tests/toee-vehicle-verdict.test.js). The DOM half below
   fills the block with textContent only; the only markup is the fixed ICONS strings.
   ============================================================ */
(function (root, factory) {
  var current = (typeof module === 'object' && module.exports)
    ? require('./toee-vehicle-current.js')
    : root && root.ToeeVehicleCurrent;
  var api = factory(root, current);
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ToeeVehicleVerdict = root.ToeeVehicleVerdict || api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (root, current) {
  'use strict';

  var BASE = '/apps/pro';
  var DEFAULT_RING_BASE = '/products/car-wheel-hub-centric-rings';
  var FITS = { fits: true, direct: true, hub_ring: true };
  var TIMEOUT_MS = 8000; // a /check slower than this counts as failed: the block stays hidden

  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) { return vars && vars[k] != null ? vars[k] : ''; });
  }

  function assign(a, b) {
    var out = {};
    Object.keys(a).forEach(function (k) { out[k] = a[k]; });
    Object.keys(b).forEach(function (k) { out[k] = b[k]; });
    return out;
  }

  /** "17″", or "19″ +2″" for a plus size: how the picker names a setup. */
  function setupName(fitment) {
    var c = current.fitmentChip(fitment);
    return c.inches + (c.tag ? ' ' + c.tag : '');
  }

  /** The hub the ring must fit: the rear one when only the rear axle needs a ring. */
  function ringHub(fitment, answer) {
    var axles = answer.axles || {};
    if (axles.rear === 'hub_ring' && axles.front !== 'hub_ring' && fitment.rearHubBoreMm != null) return fitment.rearHubBoreMm;
    return fitment.hubBoreMm;
  }

  function textOf(n) { return n == null ? null : String(n); }

  /**
   * What to show for `body` (the /vehicle/check answer) and the Current `vehicle`, or null for nothing:
   * {tone: yes|ring|no|note, key, noteKey, vars, action: {kind: change|rings|switch|browse, fitmentId?} | null}.
   */
  function verdictView(body, vehicle) {
    if (!body || body.success !== true || !body.product || !vehicle) return null;
    var kind = body.product.kind;
    if (kind !== 'tire' && kind !== 'wheel') return null;
    var fit = current.currentFitment(vehicle);
    if (!fit) return null;
    var rows = body.fitments || [];
    var mine = null;
    for (var i = 0; i < rows.length; i++) if (rows[i] && rows[i].id === fit.id) mine = rows[i];
    if (!mine) return null;

    var vars = { label: vehicle.label, usData: vehicle.market === 'US' };
    var result = mine.result;

    if (result === 'fits' || result === 'direct') {
      if (kind === 'tire') vars.size = current.sizeText(fit);
      else vars.pcd = fit.boltPattern;
      return { tone: 'yes', key: 'verdict_fits', noteKey: kind === 'tire' ? 'verdict_fits_tire_note' : 'verdict_fits_wheel_note', vars: vars, action: { kind: 'change' } };
    }

    if (result === 'hub_ring') {
      var bore = textOf(body.product.wheel && body.product.wheel.cbMm);
      var hub = textOf(ringHub(fit, mine));
      if (bore != null && hub != null) {
        vars.bore = bore;
        vars.hub = hub;
        return { tone: 'ring', key: 'verdict_ring', noteKey: 'verdict_ring_note', vars: vars, action: { kind: 'rings' } };
      }
      result = 'unknown'; // a ring answer without both bores cannot name the ring
    }

    if (result === 'unknown') {
      return { tone: 'note', key: 'verdict_unknown', noteKey: '', vars: vars, action: null };
    }

    if (result !== 'no') return null;

    var list = vehicle.fitments || [];
    for (var j = 0; j < list.length; j++) {
      if (list[j].id === fit.id) continue;
      for (var k = 0; k < rows.length; k++) {
        if (rows[k] && rows[k].id === list[j].id && FITS[rows[k].result]) {
          vars.inches = setupName(fit);
          vars.other = setupName(list[j]);
          return { tone: 'note', key: 'verdict_other', noteKey: 'verdict_other_note', vars: vars, action: { kind: 'switch', fitmentId: list[j].id } };
        }
      }
    }

    if (kind === 'tire') {
      vars.size = current.sizeText(fit);
      return { tone: 'no', key: 'verdict_no', noteKey: 'verdict_no_note', vars: vars, action: { kind: 'browse' } };
    }
    // A wheel is rejected for its diameter, bolt pattern or centre bore, and the theme cannot tell which:
    // name only what the car takes (the bolt pattern, and the smallest centre bore) and leave the verdict to /check.
    var noteKey = '';
    if (fit.boltPattern) {
      vars.pcd = fit.boltPattern;
      noteKey = 'verdict_no_wheel_bare_note';
      if (fit.hubBoreMm != null) {
        vars.hub = String(fit.hubBoreMm);
        noteKey = 'verdict_no_wheel_note';
      }
    }
    return { tone: 'no', key: 'verdict_no', noteKey: noteKey, vars: vars, action: { kind: 'browse' } };
  }

  /** A /check answer worth showing and caching: successful, with one row per Fitment of the car. */
  function usableBody(body, vehicle) {
    return !!(body && body.success === true && vehicle && Array.isArray(body.fitments) &&
      body.fitments.length === (vehicle.fitments || []).length);
  }

  function checkUrl(vehicle, productId) {
    return BASE + '/vehicle/check?year=' + encodeURIComponent(vehicle.year) + '&make=' + encodeURIComponent(vehicle.make) +
      '&model=' + encodeURIComponent(vehicle.model) + '&trim=' + encodeURIComponent(vehicle.trim) +
      '&productId=' + encodeURIComponent(productId);
  }

  function cacheKey(productId, vehicle) {
    return 'toee-vehicle-check:' + productId + ':' + [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].join('|');
  }

  /** The hub-ring product page, with the wheel bore (od) and the vehicle hub (hub) for the picker to pre-select. */
  function ringHref(baseUrl, bore, hub) {
    var base = String(baseUrl || '').trim().replace(/\?$/, '');
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'od=' + encodeURIComponent(String(bore)) + '&hub=' + encodeURIComponent(String(hub));
  }

  var api = {
    verdictView: verdictView, usableBody: usableBody, checkUrl: checkUrl, cacheKey: cacheKey, ringHref: ringHref, fill: fill,
  };

  /* ---------- the block on the page ---------- */

  var doc = typeof document === 'undefined' ? null : document;
  if (!doc || !current || !current.store) return api;
  var nodes = doc.querySelectorAll('[data-vehicle-verdict]');
  if (!nodes.length) return api;
  var store = current.store;

  var SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" aria-hidden="true">';
  var ICONS = {
    yes: SVG + '<path d="M4 12.5l5 5L20 6.5"/></svg>',
    ring: SVG + '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4"/></svg>',
    no: SVG + '<path d="M5 5l14 14M19 5L5 19"/></svg>',
    note: SVG + '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>',
  };

  // sessionStorage may throw (private mode, blocked): undefined reads, a body writes.
  function memory(key, body) {
    try {
      if (body === undefined) return JSON.parse(sessionStorage.getItem(key) || 'null');
      sessionStorage.setItem(key, JSON.stringify(body));
    } catch (e) { /* no memory: the verdict still works, it just asks again */ }
    return null;
  }

  function readStrings(el) {
    var raw = (el.querySelector('script[type="application/json"]') || {}).textContent;
    try {
      var chip = typeof ToeeVehicleChip === 'undefined' ? null : ToeeVehicleChip;
      return chip && chip.readI18n ? chip.readI18n(raw) : JSON.parse(raw || '{}');
    } catch (e) { return {}; }
  }

  function node(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function init(el) {
    var productId = el.getAttribute('data-product-id') || '';
    var ringBase = el.getAttribute('data-hub-ring-base') || DEFAULT_RING_BASE;
    var browseUrl = el.getAttribute('data-browse-url') || '/collections/all?vehicle=tires';
    var i18n = readStrings(el);
    var clip = node('div', 'toee-vehicle-verdict__clip');
    el.appendChild(clip);
    var seq = 0; // a newer vehicle drops older answers
    var pickerFromHere = false; // Change vehicle opened the picker with this block as its anchor

    function hide() {
      el.classList.remove('is-open');
      el.hidden = true;
    }

    function actionNode(view) {
      var a = view.action;
      if (!a) return null;
      var vars = view.vars;
      if (a.kind === 'change') {
        var change = node('button', 'toee-vehicle-verdict__btn toee-vehicle-verdict__btn--link', i18n.verdict_change);
        change.type = 'button';
        change.setAttribute('data-verdict-action', 'change');
        return change;
      }
      if (a.kind === 'switch') {
        var sw = node('button', 'toee-vehicle-verdict__btn toee-vehicle-verdict__btn--outline', fill(i18n.verdict_switch, vars));
        sw.type = 'button';
        sw.setAttribute('data-verdict-action', 'switch');
        sw.setAttribute('data-fitment-id', a.fitmentId);
        return sw;
      }
      if (a.kind === 'rings') {
        var rings = node('a', 'toee-vehicle-verdict__btn toee-vehicle-verdict__btn--outline', fill(i18n.verdict_ring_action, vars));
        rings.href = ringHref(ringBase, vars.bore, vars.hub);
        return rings;
      }
      var browse = node('a', 'toee-vehicle-verdict__btn toee-vehicle-verdict__btn--link', (i18n.verdict_browse || '') + ' ›');
      browse.href = browseUrl; // the snippet picks the collection: tires, steel rims, or wheels
      return browse;
    }

    function paint(view) {
      clip.textContent = '';
      var box = node('div', 'toee-vehicle-verdict__box');
      box.setAttribute('data-tone', view.tone);
      var icon = node('span', 'toee-vehicle-verdict__icon');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = ICONS[view.tone];
      var text = node('div', 'toee-vehicle-verdict__text');
      // div, never p / li: the product page styles every p and li inside .product-details with !important.
      text.appendChild(node('div', 'toee-vehicle-verdict__head', fill(i18n[view.key], view.vars)));
      var note = view.noteKey ? fill(i18n[view.noteKey], view.vars) : '';
      if (view.vars.usData && i18n.us_data) note = note ? note + ' · ' + i18n.us_data : i18n.us_data;
      if (note) text.appendChild(node('div', 'toee-vehicle-verdict__note', note));
      box.appendChild(icon);
      box.appendChild(text);
      var action = actionNode(view);
      if (action) box.appendChild(action);
      clip.appendChild(box);
    }

    // Focus was on a control that paint() is about to replace, or the picker this block opened has closed
    // and its focus return found the block hidden: put it on the new action (or the block).
    function holdsFocus() {
      var active = doc.activeElement;
      if (active && active !== doc.body) return el.contains(active);
      return pickerFromHere;
    }

    function show(view) {
      if (!view) return hide();
      var refocus = holdsFocus();
      paint(view);
      if (el.hidden) {
        el.hidden = false;
        void el.offsetHeight; // let the 350ms open start from the closed state
      }
      el.classList.add('is-open');
      if (refocus) {
        pickerFromHere = false;
        var target = clip.querySelector('[data-verdict-action], a') || el;
        target.focus({ preventScroll: true });
      }
    }

    function sync(vehicle) {
      var mine = ++seq;
      if (!vehicle || !productId) return hide();
      var key = cacheKey(productId, vehicle);
      var cached = memory(key);
      var fromCache = cached ? verdictView(cached, vehicle) : null;
      if (fromCache) return show(fromCache); // a stored body that gives no verdict counts as a miss
      hide(); // an answer for the previous car must not stay up while this one loads
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
      fetch(checkUrl(vehicle, productId), { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) {
          if (!res.ok) throw new Error('check');
          return res.json();
        })
        .then(function (body) {
          clearTimeout(timer);
          if (mine !== seq) return;
          if (!usableBody(body, vehicle)) throw new Error('check');
          memory(key, body);
          show(verdictView(body, vehicle));
        })
        .catch(function () {
          clearTimeout(timer);
          if (mine === seq) hide();
        });
    }

    el.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-verdict-action]') : null;
      if (!btn || !el.contains(btn)) return;
      var action = btn.getAttribute('data-verdict-action');
      if (action === 'change') {
        var chip = typeof ToeeVehicleChip === 'undefined' ? null : ToeeVehicleChip;
        // The block itself is the anchor: the picker's focus return needs an element that survives a repaint.
        if (chip && chip.openPicker) {
          pickerFromHere = true;
          chip.openPicker(el);
        }
      } else if (action === 'switch') {
        var vehicle = store.read();
        if (vehicle) store.save(assign(vehicle, { fitmentId: btn.getAttribute('data-fitment-id') }));
      }
    });

    store.onChange(sync);
    // Restored from the back-forward cache: the vehicle may have changed while the page was away.
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) sync(store.read());
    });
    sync(store.read());
  }

  Array.prototype.forEach.call(nodes, init);

  return api;
});
